import { zodResolver } from "@hookform/resolvers/zod";
import { InputAdornment, Stack, TextField } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { type UseModalState } from "@src/lib/ui-react";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { DialogFooter } from "../../components/DialogFooter";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { Text } from "../../components/Text";
import { useCreateWorkplaceChargeLeadTimeDifferential } from "../api/useCreateWorkplaceChargeLeadTimeDifferential";
import {
  MAX_DIFFERENTIAL_PERCENT,
  MAX_THRESHOLD_HOURS,
  MIN_DIFFERENTIAL_PERCENT,
  MIN_THRESHOLD_HOURS,
} from "../constants";
import {
  type AddDifferentialFormInput,
  type AddDifferentialFormOutput,
  type ExistingDifferential,
  getDefaultFormValues,
  makeAddDifferentialFormSchema,
} from "./addLeadTimeDifferentialSchema";

interface AddLeadTimeDifferentialDialogProps {
  workplaceId: string;
  modalState: UseModalState;
  existingDifferentials: ExistingDifferential[];
}

export function AddLeadTimeDifferentialDialog(props: AddLeadTimeDifferentialDialogProps) {
  const { workplaceId, modalState, existingDifferentials } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const { mutateAsync: createDifferential, isLoading } =
    useCreateWorkplaceChargeLeadTimeDifferential();

  const resolver = useMemo(
    () => zodResolver(makeAddDifferentialFormSchema(existingDifferentials)),
    [existingDifferentials]
  );

  const defaultFormValues = useMemo(
    () => getDefaultFormValues(existingDifferentials),
    [existingDifferentials]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<AddDifferentialFormInput, unknown, AddDifferentialFormOutput>({
    resolver,
    defaultValues: defaultFormValues,
    mode: "onChange",
  });

  const watchedThreshold = useWatch({ control, name: "thresholdInHours" });
  const watchedDifferential = useWatch({ control, name: "differentialPercent" });

  useEffect(() => {
    if (String(watchedThreshold) === "" && String(watchedDifferential) === "") {
      return;
    }
    void trigger();
  }, [watchedThreshold, watchedDifferential, existingDifferentials, trigger]);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      reset(defaultFormValues);
    }
  }, [modalState.modalIsOpen, reset, defaultFormValues]);

  const handleClose = useCallback(() => {
    reset(defaultFormValues);
    modalState.closeModal();
  }, [modalState, reset, defaultFormValues]);

  const onSubmit = useCallback(
    async (values: AddDifferentialFormOutput) => {
      try {
        await createDifferential({
          workplaceId,
          thresholdInHours: values.thresholdInHours,
          incrementalMultiplier: values.differentialPercent / 100,
        });
        showSuccessToast("Tier added");
        handleClose();
      } catch (error) {
        let errorMessage = "Failed to add tier";
        if (isAxiosError<{ message?: string }>(error) && error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        showErrorToast(errorMessage);
      }
    },
    [createDifferential, handleClose, showErrorToast, showSuccessToast, workplaceId]
  );

  return (
    <ResponsiveDialog
      showCloseButton
      modalState={modalState}
      title="Add tier"
      width={420}
      footerContent={
        <DialogFooter
          primaryAction={{
            label: "Create",
            onClick: handleSubmit(onSubmit),
            isLoading: isLoading || isSubmitting,
            disabled: !isValid,
          }}
          secondaryAction={{
            label: "Cancel",
            disabled: isLoading || isSubmitting,
            onClick: handleClose,
          }}
        />
      }
    >
      <Stack spacing={4}>
        <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
          Shifts created within the threshold of start time will have the charge differential added
          on top of the Workplace Base Charge.
        </Text>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            Threshold (hours)
          </Text>
          <Controller
            control={control}
            name="thresholdInHours"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                disabled={isLoading || isSubmitting}
                error={Boolean(errors.thresholdInHours)}
                helperText={
                  errors.thresholdInHours?.message ??
                  `Between ${MIN_THRESHOLD_HOURS} and ${MAX_THRESHOLD_HOURS} hours.`
                }
                inputProps={{
                  inputMode: "numeric",
                  min: MIN_THRESHOLD_HOURS,
                  max: MAX_THRESHOLD_HOURS,
                  step: 1,
                }}
                placeholder="e.g. 12"
                size="small"
                type="number"
              />
            )}
          />
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            Charge differential on top of base charge
          </Text>
          <Controller
            control={control}
            name="differentialPercent"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                disabled={isLoading || isSubmitting}
                error={Boolean(errors.differentialPercent)}
                helperText={
                  errors.differentialPercent?.message ??
                  `Between ${MIN_DIFFERENTIAL_PERCENT}% and ${MAX_DIFFERENTIAL_PERCENT}%.`
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  inputMode: "numeric",
                  min: MIN_DIFFERENTIAL_PERCENT,
                  max: MAX_DIFFERENTIAL_PERCENT,
                  step: 1,
                }}
                placeholder="e.g. 5"
                size="small"
                type="number"
              />
            )}
          />
        </Stack>
      </Stack>
    </ResponsiveDialog>
  );
}
